import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { mediaQueriesBP } from "@solveindustrial/Utilities/variables";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    signInUrl?: string;
    extendedStyles?: LogInForPriceButtonStyles;
    showContactUs?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = state.context.session;

    const { contactUsPageUrl } = getSettingsCollection(state).customOrderSettings as any;

    return {
        // TODO: This is a temporary fix to get the contact us link to work. We need to refactor the contact us link to use the new page custom link system.
        contactUsPageUrl,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest, // Verndale Customization
    };
};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps>;

export interface LogInForPriceButtonStyles {
    container?: GridContainerProps;
    guestButtonStyles?: ButtonPresentationProps;
    contactUsGridItem?: GridItemProps;
    contacUsLabel?: TypographyPresentationProps;
}

export const logInForPriceButtonStyles: LogInForPriceButtonStyles = {
    container: {
        gap: 10,
        css: css`
            flex-direction: column;

            .contact-us-label {
                margin-block-start: 20px;
            }
        `,
    },

    guestButtonStyles: {
        variant: "secondary",
        typographyProps: {
            size: 16,
            css: css`
                ${mediaQueriesBP.minMD} {
                    font-size: 16px;
                    line-height: 22px;
                }
            `,
        },
        css: css`
            width: max-content;
            min-height: 30px;
            height: 30px;
            padding: 0 16px;

            ${mediaQueriesBP.minMD} {
                min-width: 150px;
                min-height: 30px;
                height: 30px;
                padding: 0 16px;
            }
        `,
    },

    contactUsGridItem: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;

            .product-share-link-non-auth {
                [data-test-selector="productShareLink"] {
                    display: block !important;
                }
            }
        `,
    },

    contacUsLabel: {
        variant: "p",
        css: css`
            margin: 0;
            text-align: left;
        `,
    },
};

const LogInForPricingButton = ({
    signInUrl,
    currentLocation,
    extendedStyles,
    history,
    showContactUs,
    contactUsPageUrl,
    isAuthenticated,
}: Props) => {
    const [styles] = useState(() => mergeToNew(logInForPriceButtonStyles, extendedStyles));

    const returnUrl = currentLocation.pathname + currentLocation.search;
    const redirectUrl =
        returnUrl === "/" ? signInUrl ?? "/" : `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect`;

    if (showContactUs) {
        return (
            <GridContainer {...styles.container}>
                <GridItem width={12}>
                    <Button
                        onClick={() => {
                            history.push(redirectUrl);
                        }}
                        data-test-selector="guestButton"
                        {...styles.guestButtonStyles}
                    >
                        {translate("Log in for Pricing")}
                    </Button>
                </GridItem>
                <GridItem width={12} className="contact-us-label">
                    <Typography {...styles.contacUsLabel}>
                        {siteMessage(
                            "Not a distributor? Reach out to a Solve sales representative for pricing and availability.",
                        )}
                    </Typography>
                </GridItem>
                <GridItem width={12} {...styles.contactUsGridItem}>
                    <Button
                        onClick={() => {
                            const path = new URL(contactUsPageUrl).pathname;
                            history.push(path ?? "/");
                        }}
                        data-test-selector="contactUsButton"
                        {...styles.guestButtonStyles}
                    >
                        {translate("Contact Us")}
                    </Button>
                    <div className="product-share-link-non-auth">
                        {createWidgetElement("ProductDetails/ProductDetailsShareLink", {
                            fields: {
                                text: translate("Share"),
                            },
                        })}
                    </div>
                </GridItem>
            </GridContainer>
        );
    }

    return (
        <Button
            onClick={() => {
                history.push(redirectUrl);
            }}
            data-test-selector="guestButton"
            {...styles.guestButtonStyles}
        >
            {translate("Log in for Pricing")}
        </Button>
    );
};

const ConnectedComponent = connect(mapStateToProps, {})(withHistory(LogInForPricingButton));
export default ConnectedComponent;
